import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import '../common.css';

// Staff role options
const staffRoles = [
  { id: "tour-manager", label: "Tour Manager" },
  { id: "production-manager", label: "Production Manager" },
  { id: "technical-director", label: "Technical Director" },
  { id: "sound-engineer", label: "Sound Engineer" },
  { id: "lighting-tech", label: "Lighting Technician" },
  { id: "stage-manager", label: "Stage Manager" },
  { id: "logistics-coordinator", label: "Logistics Coordinator" },
  { id: "merch-manager", label: "Merchandise Manager" },
  { id: "security", label: "Security Personnel" },
  { id: "admin-assistant", label: "Administrative Assistant" },
  { id: "marketing-onsite", label: "On-site Marketing Staff" },
  { id: "other", label: "Other" }
];

// Hiring options
const hiringOptions = [
  { id: "existing", label: "Existing team members" },
  { id: "local", label: "Local hires at each location" },
  { id: "travel", label: "Traveling with tour" },
  { id: "venue", label: "Venue-provided staff" },
  { id: "contractor", label: "Independent contractors" },
  { id: "agency", label: "Staffing agency" },
  { id: "volunteer", label: "Volunteers" },
  { id: "remote", label: "Remote workers" }
];

// Required certification options
const certificationOptions = [
  { id: "first-aid", label: "First Aid/CPR" },
  { id: "security", label: "Security certifications" },
  { id: "technical", label: "Technical certifications" },
  { id: "health-safety", label: "Health & safety" },
  { id: "food-handling", label: "Food handling" },
  { id: "driving", label: "Special driving licenses" },
  { id: "covid", label: "COVID-19 compliance" },
  { id: "accessibility", label: "Accessibility training" },
  { id: "other", label: "Other" }
];

const PersonnelStaffing = ({ formData, onInputChange, onNext, onPrev, error }) => {
  const [additionalRole, setAdditionalRole] = useState('');
  const [additionalCount, setAdditionalCount] = useState(1);
  const [staffRoleError, setStaffRoleError] = useState('');
  
  // Get key staff roles data
  const getKeyStaff = () => {
    return formData.keyStaff || [];
  };
  
  // Handle checkbox changes for multi-select options
  const handleMultiSelectChange = (field, value, isChecked) => {
    const currentValues = formData[field] || [];
    let newValues;
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }
    
    onInputChange({ [field]: newValues });
  };

  // Handle input changes for text fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
  };

  // Check if a value is selected in a multi-select list
  const isSelected = (field, value) => {
    return (formData[field] || []).includes(value);
  };
  
  // Add staff role to key staff
  const addStaffRole = () => {
    if (!additionalRole.trim()) {
      setStaffRoleError("Please select a role");
      return;
    }
    
    if (additionalCount < 1) {
      setStaffRoleError("Count must be at least 1");
      return;
    }
    
    setStaffRoleError('');
    
    const keyStaff = getKeyStaff();
    const existingRoleIndex = keyStaff.findIndex(staff => staff.role === additionalRole);
    
    if (existingRoleIndex >= 0) {
      // Update existing role
      const updatedKeyStaff = [...keyStaff];
      updatedKeyStaff[existingRoleIndex].count = additionalCount;
      onInputChange({ keyStaff: updatedKeyStaff });
    } else {
      // Add new role
      const staffLabel = staffRoles.find(role => role.id === additionalRole)?.label || additionalRole;
      
      onInputChange({
        keyStaff: [
          ...keyStaff,
          { role: additionalRole, roleLabel: staffLabel, count: additionalCount }
        ]
      });
    }
    
    setAdditionalRole('');
    setAdditionalCount(1);
  };
  
  // Remove a staff role
  const removeStaffRole = (roleIndex) => {
    const keyStaff = getKeyStaff();
    
    onInputChange({
      keyStaff: keyStaff.filter((_, index) => index !== roleIndex)
    });
  };
  
  // Custom role handling
  const handleCustomRole = (value) => {
    setAdditionalRole(value);
    
    if (value === 'other') {
      // When "Other" is selected, allow custom label entry
      if (!formData.customRoleLabel) {
        onInputChange({ customRoleLabel: '' });
      }
    } else {
      // Clear custom label for non-custom selections
      onInputChange({ customRoleLabel: '' });
    }
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <h4 className="mb-0 text-white">Define Your Team Structure</h4>
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        
        <Form>
          {/* Key Staff Positions Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Key Staff Positions</h5>
            
            <div className="mb-3">
              <Card className="dark-card-alt">
                <Card.Body className="p-3">
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="text-white">Role</Form.Label>
                        <Form.Select
                          className="dark-input"
                          value={additionalRole}
                          onChange={(e) => handleCustomRole(e.target.value)}
                        >
                          <option value="">Select role...</option>
                          {staffRoles.map(role => (
                            <option key={role.id} value={role.id}>{role.label}</option>
                          ))}
                        </Form.Select>
                        {additionalRole === 'other' && (
                          <Form.Control
                            type="text"
                            className="dark-input mt-2"
                            placeholder="Specify role"
                            value={formData.customRoleLabel || ''}
                            onChange={(e) => onInputChange({ customRoleLabel: e.target.value })}
                          />
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-white">Number required</Form.Label>
                        <Form.Control
                          type="number"
                          className="dark-input"
                          min="1"
                          value={additionalCount}
                          onChange={(e) => setAdditionalCount(parseInt(e.target.value) || 1)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button 
                        variant="success"
                        className="w-100"
                        onClick={addStaffRole}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </Col>
                  </Row>
                  {staffRoleError && <Alert variant="danger" className="mt-2 p-2 small">{staffRoleError}</Alert>}
                </Card.Body>
              </Card>
            </div>
            
            {getKeyStaff().length > 0 ? (
              <Table responsive variant="dark" className="dark-table mt-3">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Number</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {getKeyStaff().map((staff, index) => (
                    <tr key={index}>
                      <td>
                        {staff.role === 'other' ? formData.customRoleLabel || 'Custom role' : staff.roleLabel}
                      </td>
                      <td>{staff.count}</td>
                      <td className="text-center">
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => removeStaffRole(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted mt-3 text-center">No key staff positions added yet.</p>
            )}
          </div>
          
          {/* Hiring Approach */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Hiring Approach</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Preferred hiring sources</Form.Label>
              <Row>
                {hiringOptions.map(option => (
                  <Col md={6} key={option.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`hiring-${option.id}`}
                      className="dark-checkbox"
                      label={option.label}
                      checked={isSelected('hiringSources', option.id)}
                      onChange={(e) => handleMultiSelectChange('hiringSources', option.id, e.target.checked)}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Specialized skills required</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                className="dark-input"
                placeholder="List any specialized skills needed from staff"
                value={formData.specializedSkills || ''}
                onChange={(e) => handleInputChange('specializedSkills', e.target.value)}
              />
            </Form.Group>
          </div>
          
          {/* Training & Certification Requirements */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Training & Certification Requirements</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Required certifications</Form.Label>
              <Row>
                {certificationOptions.slice(0, certificationOptions.length - 1).map(option => (
                  <Col md={6} key={option.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`cert-${option.id}`}
                      className="dark-checkbox"
                      label={option.label}
                      checked={isSelected('requiredCertifications', option.id)}
                      onChange={(e) => handleMultiSelectChange('requiredCertifications', option.id, e.target.checked)}
                    />
                  </Col>
                ))}
                
                <Col md={6} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="cert-other"
                    className="dark-checkbox"
                    label="Other"
                    checked={isSelected('requiredCertifications', 'other')}
                    onChange={(e) => handleMultiSelectChange('requiredCertifications', 'other', e.target.checked)}
                  />
                </Col>
                <Col md={6}>
                  {isSelected('requiredCertifications', 'other') && (
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="Specify other certification"
                      value={formData.otherCertification || ''}
                      onChange={(e) => handleInputChange('otherCertification', e.target.value)}
                    />
                  )}
                </Col>
              </Row>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Pre-tour training required</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                className="dark-input"
                placeholder="Describe any pre-tour training needs"
                value={formData.preTourTraining || ''}
                onChange={(e) => handleInputChange('preTourTraining', e.target.value)}
              />
            </Form.Group>
          </div>
          
          {/* Communication & Management */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Communication & Management</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Team management structure</Form.Label>
              <Form.Select
                className="dark-input"
                value={formData.managementStructure || ''}
                onChange={(e) => handleInputChange('managementStructure', e.target.value)}
              >
                <option value="">Select structure...</option>
                <option value="hierarchical">Hierarchical (traditional)</option>
                <option value="flat">Flat/collaborative</option>
                <option value="functional">Functional (by specialty area)</option>
                <option value="matrix">Matrix (dual reporting)</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Primary communication methods</Form.Label>
              <Row>
                <Col md={4} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="comm-daily-briefing"
                    className="dark-checkbox"
                    label="Daily briefings"
                    checked={formData.dailyBriefings || false}
                    onChange={(e) => handleInputChange('dailyBriefings', e.target.checked)}
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="comm-digital-platform"
                    className="dark-checkbox"
                    label="Digital platform"
                    checked={formData.digitalPlatform || false}
                    onChange={(e) => handleInputChange('digitalPlatform', e.target.checked)}
                  />
                </Col>
                <Col md={4} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="comm-staff-meetings"
                    className="dark-checkbox"
                    label="Regular staff meetings"
                    checked={formData.staffMeetings || false}
                    onChange={(e) => handleInputChange('staffMeetings', e.target.checked)}
                  />
                </Col>
              </Row>
              
              {formData.digitalPlatform && (
                <Form.Control
                  type="text"
                  className="dark-input mt-2"
                  placeholder="Specify digital platform (e.g., Slack, Microsoft Teams)"
                  value={formData.digitalPlatformName || ''}
                  onChange={(e) => handleInputChange('digitalPlatformName', e.target.value)}
                />
              )}
            </Form.Group>
          </div>
          
          {/* Additional Personnel Notes */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Personnel Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder="Any other staffing or personnel considerations"
              value={formData.additionalPersonnelNotes || ''}
              onChange={(e) => handleInputChange('additionalPersonnelNotes', e.target.value)}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-light"
              className="dark-outline-button"
              onClick={onPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
              Back
            </Button>
            <Button
              className="dark-primary-button"
              onClick={onNext}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PersonnelStaffing;